import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useFetchDataId } from 'services/queries';
import { useParams } from "react-router-dom";
import { useQueryClient } from '@tanstack/react-query';
import SummaryHeader from '../sections/common/SummaryHeader';
import CustomsValueSection from '../sections/common/CustomsValueSection';
import CustomsTaxesSection from '../sections/common/CustomsTaxesSection';
import EncomendaInvoiceSection from '../sections/encomenda/EncomendaInvoiceSection';
import ExitTaxesSection from '../sections/encomenda/ExitTaxesSection';
import MarginDisbursementSection from '../sections/common/MarginDisbursementSection';
import TaxCreditsSection from '../sections/common/TaxCreditsSection';
import ProductTable from '../sections/common/ProductTable';
import NoTaxCalculationPrompt from '../components/NoTaxCalculationPrompt';

/**
 * EncomendaView - Strategy component for Encomenda (Sales Order) order type
 * Includes exit/output taxes section specific to Encomenda orders
 * @param {Object} props - Component props
 * @param {Object} props.order - Order data
 */
const EncomendaView = ({ order }) => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { data: taxCalculation, error, isLoading } = useFetchDataId(`tax/order-tax-calculation/latest`, id, true);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading tax calculation data. Please try again later.
      </Alert>
    );
  }

  if (taxCalculation && Object.keys(taxCalculation).length === 0) {
    return (
      <NoTaxCalculationPrompt
        order={order}
        onCalculationComplete={() => {
          queryClient.invalidateQueries(['tax/order-tax-calculation/latest', id]);
        }}
      />
    );
  }

  const combinedData = {
    ...order,
    latest_tax_calculation: taxCalculation
  };

  const exchangeRate = taxCalculation?.exchange_rate_dollar_to_real || 1;

  return (
    <Box p={2}>
      <Box className="print-section page-break-inside-avoid summary-grid">
        <SummaryHeader taxCalculation={taxCalculation} order={order} />
      </Box>

      <Box className="print-section page-break-inside-avoid">
        <CustomsValueSection order={combinedData} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <CustomsTaxesSection order={combinedData} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <EncomendaInvoiceSection order={combinedData} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <ExitTaxesSection order={combinedData} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <MarginDisbursementSection order={combinedData} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <TaxCreditsSection order={combinedData} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <ProductTable order={combinedData} exchangeRate={exchangeRate} />
      </Box>
    </Box>
  );
};

export default EncomendaView;
